import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Package, FileText, Search, CheckCircle } from 'lucide-react';

/**
 * EmptyState - Standardized empty state component
 * 
 * @param {React.ElementType} icon - Icon to display
 * @param {string} title - Main message
 * @param {string} description - Secondary message
 * @param {string} actionLabel - Button label
 * @param {function} onAction - Button click handler
 * @param {string} variant - 'default' | 'success' | 'search'
 * @param {string} className - Additional classes
 */
export function EmptyState({
  icon: Icon = Package,
  title,
  description,
  actionLabel,
  onAction,
  variant = 'default',
  className,
  children,
  ...props
}) {
  const variantStyles = {
    default: {
      iconClass: 'text-slate-300',
      bgClass: '',
    },
    success: {
      iconClass: 'text-emerald-400',
      bgClass: 'bg-emerald-50/50',
    },
    search: {
      iconClass: 'text-slate-300',
      bgClass: '',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center py-16 px-6 text-center",
        styles.bgClass,
        className
      )}
      data-testid="empty-state"
      {...props}
    >
      <Icon 
        className={cn("h-16 w-16 mb-4", styles.iconClass)} 
        strokeWidth={1.25}
      />
      {title && (
        <p className="text-lg font-semibold text-slate-700 mb-2">
          {title}
        </p>
      )}
      {description && (
        <p className="text-sm text-slate-500 max-w-sm mb-6">
          {description}
        </p>
      )}
      {actionLabel && onAction && (
        <Button
          onClick={onAction}
          className="bg-slate-800 hover:bg-slate-700"
          data-testid="empty-state-action"
        >
          {actionLabel}
        </Button>
      )}
      {children}
    </div>
  );
}

/**
 * Pre-configured empty states
 */
export function EmptyStateNoResults({ searchTerm, onClear }) {
  return (
    <EmptyState
      icon={Search}
      title="Nenhum resultado encontrado"
      description={searchTerm ? `Não encontrámos resultados para "${searchTerm}"` : 'Tente ajustar os filtros'}
      actionLabel={onClear ? 'Limpar filtros' : undefined}
      onAction={onClear}
      variant="search"
    />
  );
}

export function EmptyStateAllClear({ title = "Tudo em dia", description }) {
  return (
    <EmptyState
      icon={CheckCircle}
      title={title}
      description={description}
      variant="success"
    />
  );
}

export function EmptyStateNoData({ title = "Sem dados", description, actionLabel, onAction }) {
  return (
    <EmptyState
      icon={FileText}
      title={title}
      description={description}
      actionLabel={actionLabel}
      onAction={onAction}
    />
  );
}

export default EmptyState;
